import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { apiRequest } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { ThemeContext } from './ThemeContextValue'
import { applyTheme, getActiveTheme, normalizeTheme, type PortalTheme } from './theme'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { token, profile, setAuth } = useAuth()
  // Skrypt pre-paint w index.html ustawił już data-theme przed startem Reacta.
  const [theme, setThemeState] = useState<PortalTheme>(() => getActiveTheme())

  const setLocalTheme = useCallback((nextTheme: PortalTheme) => {
    applyTheme(nextTheme)
    setThemeState(nextTheme)
  }, [])

  const persistThemePreference = useCallback(
    async (nextTheme: PortalTheme) => {
      if (!token || !profile) {
        return
      }
      const currentSettings =
        profile.settings && typeof profile.settings === 'object' ? profile.settings : {}
      const currentUserSettings =
        currentSettings.user && typeof currentSettings.user === 'object'
          ? currentSettings.user
          : {}
      if (normalizeTheme(currentUserSettings.theme) === nextTheme) {
        return
      }

      const nextUserSettings = {
        ...currentUserSettings,
        theme: nextTheme,
      }

      try {
        const settings = await apiRequest<Record<string, unknown>>('/api/settings', {
          method: 'PATCH',
          token,
          body: {
            user: nextUserSettings,
          },
        })
        setAuth(token, {
          ...profile,
          settings:
            settings && typeof settings === 'object'
              ? settings
              : {
                  ...currentSettings,
                  user: nextUserSettings,
                },
        })
      } catch {
        // Motyw pozostaje aktywny lokalnie nawet gdy zapis preferencji zawiedzie.
      }
    },
    [profile, setAuth, token]
  )

  const setTheme = useCallback(
    (nextTheme: PortalTheme) => {
      setLocalTheme(nextTheme)
      void persistThemePreference(nextTheme)
    },
    [persistThemePreference, setLocalTheme]
  )

  const value = useMemo(
    () => ({ theme, setTheme, setLocalTheme }),
    [theme, setTheme, setLocalTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
